import React from 'react';
import PropTypes from 'prop-types';

import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Divider from '@material-ui/core/Divider';
import ChatBubbleIcon from '@material-ui/icons/ChatBubbleOutline';
import CheckIcon from '@material-ui/icons/Done';

import { Wizard, Steps, Step } from 'react-albus';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import ErrorDialog from '../../../components/ErrorDialog/ErrorDialog';
import LoaderProgress from '../../../components/Loader/LoaderProgress/LoaderProgress.js';
import { getString } from '../../../helpers/stringHelpers.js';
import Form from '../Form/Form.js';

import { withStyles } from '@material-ui/core/styles';
import styles from './styles.js';

const stringsComponent = 'survey';

class SurveyModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      open: true,
      isLoading: false,
      hasError: false,
      formData: [],
    };

    this.handleClose = this.handleClose.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleLoading = this.handleLoading.bind(this);
    this.handleFormProgression = this.handleFormProgression.bind(this);
  }

  componentDidUpdate(prevProps) {
    // If the path changes, the form should be shown again.
    if (prevProps.path != this.props.path) {
      this.setState({ open: true });
    }
  }

  handleLoading() {
    this.setState({ isLoading: true });
  }

  handleClose() {
    const { onClose } = this.props;

    this.setState({ open: false });

    onClose();
  }

  handleCancel() {
    const { onCancel } = this.props;

    this.setState({ open: false });

    onCancel();
  }

  handleFormProgression(values, action, formKey) {
    let formData = this.state.formData;
    formData[formKey] = values;
    this.setState({ formData: formData });
    action();
  }

  makeDialogTitle(title, icon) {
    const { classes } = this.props;

    const Icon = icon;

    return (
      <DialogTitle className={classes.dialogTitle} disableTypography={true}>
        <div>
          <div className={classes.iconContainer}>
            <Icon className={classes.icon} />
          </div>
          <Typography variant="h3" className={classes.title}>
            {title}
          </Typography>
        </div>
      </DialogTitle>
    );
  }

  render() {
    const {
      classes,
      confirmationHeader,
      confirmationBody,
      id,
      form,
      failureText,
      handleSurveyComplete,
    } = this.props;

    const { open, isLoading, hasError } = this.state;

    return (
      <div className={classes.root}>
        <LoaderProgress show={isLoading} />
        {hasError && (
          <ErrorDialog
            logout={this.handleClose}
            text={failureText}
            primaryButtonLabel={getString(stringsComponent, 'CLOSE_LABEL')}
          />
        )}
        <Dialog
          open={open}
          classes={{
            root: isLoading ? classes.loadingDialog : null,
            paper: !isLoading ? classes.dialogPaper : null,
          }}
          disableEnforceFocus
        >
          <Wizard>
            {
              <Steps>
                <Step
                  id="form"
                  render={({ next }) => (
                    <React.Fragment>
                      <div className={classes.iconContainer}>
                        <ChatBubbleIcon className={classes.icon} />
                      </div>
                      <DialogContent className={classes.dialogContent}>
                        <Form
                          form={form}
                          formId={id}
                          onCancel={() => {
                            this.handleCancel();
                          }}
                          onComplete={() => {
                            this.setState({ isLoading: false });
                            handleSurveyComplete(id);
                            next();
                          }}
                          onLoading={this.handleLoading}
                        />
                      </DialogContent>
                    </React.Fragment>
                  )}
                />
                <Step
                  id="thanks"
                  render={() => (
                    <React.Fragment>
                      <div className={classes.titleContentContainer}>
                        {this.makeDialogTitle(confirmationHeader, CheckIcon)}
                        <DialogContent className={classes.dialogContent}>
                          <Typography
                            component="div"
                            className={classes.documentStyle}
                          >
                            {parse(DOMPurify.sanitize(confirmationBody))}
                          </Typography>
                        </DialogContent>
                      </div>
                      <DialogActions className={classes.dialogActions}>
                        <Divider />
                        <Button
                          autoFocus
                          onClick={this.handleClose}
                          variant="outlined"
                          color="primary"
                          className={classes.button}
                        >
                          {getString(stringsComponent, 'CLOSE_LABEL')}
                        </Button>
                      </DialogActions>
                    </React.Fragment>
                  )}
                />
              </Steps>
            }
          </Wizard>
        </Dialog>
      </div>
    );
  }
}

SurveyModal.propTypes = {
  classes: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  confirmationHeader: PropTypes.string.isRequired,
  confirmationBody: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  failureText: PropTypes.object.isRequired,
};

SurveyModal.defaultProps = {};

export default withStyles(styles)(SurveyModal);
